// ========================================
// PHASE 4: ARCHITECTURE FLEXIBLE
// ========================================

// Types de base
export type FlexibleChartType = 'line' | 'bar' | 'pie' | 'doughnut' | 'area'
export type CoordinateSystem = 'cartesian' | 'polar'
export type CartesianChartType = 'line' | 'bar' | 'area'
export type PolarChartType = 'pie' | 'doughnut'
export type DataSource = 'sales' | 'products'
export type AggregationType = 'sum' | 'count' | 'average' | 'median' | 'max' | 'min' | 'std_dev' | 'variance' | 'first' | 'last' | 'percentile_25' | 'percentile_75' | 'percentile_90' | 'percentile_95'
export type TimeAggregation = 'hour' | 'day' | 'week' | 'month' | 'year' | 'hour_distribution' | 'weekday' | 'month_day'

// Configuration des filtres
export interface FilterConfig {
  families?: string[]
  products?: string[]
  dateRange?: { start: Date; end: Date }
  customFilters?: { field: string; operator: 'eq' | 'gt' | 'lt' | 'in'; value: any }[]
}

// Configuration d'affichage
export interface DisplayConfig {
  title?: string
  showLegend: boolean
  colors?: string[]
  theme?: 'light' | 'dark' | 'auto'
  responsive: boolean
  mobileOptimized: boolean // Optimisations spécifiques mobile
}

// Configuration mobile
export interface MobileConfig {
  touchOptimized: boolean
  compactMode: boolean // Interface réduite pour petits écrans
  swipeNavigation: boolean
  bottomSheetMode: boolean // Utiliser des bottom sheets pour sélecteurs
}

// États et erreurs
export interface ChartState {
  loading: boolean
  error?: string
  data?: any[]
  lastUpdated?: Date
  syncStatus?: 'synced' | 'pending' | 'offline'
}

// Configuration de prévisualisation
export interface PreviewConfig {
  enabled: boolean
  sampleSize: number // Limite pour l'aperçu (performance mobile)
  refreshDelay: number // Debounce pour éviter trop de requêtes
}

// Configuration des axes
export interface AxisConfig {
  type: 'category' | 'linear' | 'time'
  field: string
  label: string
  format?: string // Ex: 'currency', 'percentage'
  // Agrégation temporelle intelligente pour axe temps
  timeAggregation?: TimeAggregation
}

// Configuration temporelle
export interface TimeAggregationConfig {
  type: TimeAggregation
  label: string
  description: string
  smartDetection: boolean // Auto-détection basée sur la plage de dates
}

// Configuration d'agrégation statistique
export interface AggregationConfig {
  type: AggregationType
  label: string
  description: string
  useCases: string[]
  compatibleFields: string[] // Types de champs compatibles
}

// Source de données flexible
export interface DataSourceConfig {
  id: string
  name: string // Nom affiché dans la légende
  source: DataSource
  aggregation: AggregationType // Fonction d'agrégation statistique
  field: string // Ex: 'totalSales', 'quantity'
  color?: string
  filters?: FilterConfig[]
  // Performance mobile
  limit?: number // Limite d'éléments pour performance
  offset?: number // Pour pagination
  yAxisIndex?: 0 | 1 // 0 = axe principal, 1 = axe secondaire
  // Paramètres API pour source des données
  origin: 'web' | 'all' // web = ventes web seulement, all = web + centre
  deposit: boolean // true = données d'acomptes, false = données de ventes
  // Nouvelle architecture : type de graphique par série
  chartType?: FlexibleChartType // Type de visualisation pour cette série (optionnel pour rétrocompatibilité)
}

// Configuration principale flexible
export interface FlexibleChartConfig {
  // Configuration graphique
  chartType: FlexibleChartType // Gardé pour rétrocompatibilité (sera déprécié)
  coordinateSystem?: CoordinateSystem // Nouveau système : définit le type de canvas
  
  // Sources de données
  dataSources: DataSourceConfig[]
  
  // Configuration des axes (avec support axes doubles)
  xAxis: AxisConfig
  yAxis: AxisConfig
  yAxis2?: AxisConfig // Axe secondaire pour graphiques multi-métriques
  
  // Filtres
  filters: FilterConfig
  
  // Style et affichage
  display: DisplayConfig
  
  // Configuration spécifique mobile
  mobileConfig: MobileConfig

  // Période (gardée pour compatibilité avec l'existant)
  period: {
    start: string
    end: string
  }
  showComparison?: boolean
  comparisonPeriod?: {
    start: string
    end: string
  }
}

// Modèle de graphique personnalisé
export interface CustomChart {
  id: string
  name: string
  config: FlexibleChartConfig
  state: ChartState
  // Liaison au rapport
  centerId: number
  reportId: string
  createdAt: Date
  updatedAt: Date
}

// ========================================
// CONSTANTES ET CONFIGURATIONS
// ========================================

// Groupes de compatibilité des types de graphiques
export const COORDINATE_SYSTEMS = {
  cartesian: {
    name: 'Analyses temporelles',
    description: 'Graphiques avec axes X/Y pour analyser les évolutions dans le temps',
    icon: '📊',
    types: ['line', 'bar', 'area'] as CartesianChartType[],
    supportsMultipleSeries: true,
    supportsTimeAxis: true,
    supportsSecondaryAxis: true
  },
  polar: {
    name: 'Répartitions',
    description: 'Graphiques circulaires pour analyser les proportions et parts',
    icon: '🥧',
    types: ['pie', 'doughnut'] as PolarChartType[],
    supportsMultipleSeries: false, // Une seule série pour les graphiques polaires
    supportsTimeAxis: false,
    supportsSecondaryAxis: false
  }
} as const

// Mapping des types individuels vers leur système de coordonnées
export const CHART_TYPE_TO_COORDINATE_SYSTEM: Record<FlexibleChartType, CoordinateSystem> = {
  'line': 'cartesian',
  'bar': 'cartesian', 
  'area': 'cartesian',
  'pie': 'polar',
  'doughnut': 'polar'
}

// Valide si un type de graphique est compatible avec un système de coordonnées
export function isChartTypeCompatible(chartType: FlexibleChartType, coordinateSystem: CoordinateSystem): boolean {
  return CHART_TYPE_TO_COORDINATE_SYSTEM[chartType] === coordinateSystem
}

// Obtient les types de graphiques disponibles pour un système de coordonnées
export function getAvailableChartTypes(coordinateSystem: CoordinateSystem): FlexibleChartType[] {
  return COORDINATE_SYSTEMS[coordinateSystem].types
}

// Détecte automatiquement le système de coordonnées basé sur les types des séries
export function detectCoordinateSystem(dataSources: DataSourceConfig[]): CoordinateSystem {
  if (!dataSources.length) return 'cartesian' // Par défaut
  
  // Si toutes les séries ont un type polaire, utiliser polar
  const allPolar = dataSources.every(ds => 
    ds.chartType && CHART_TYPE_TO_COORDINATE_SYSTEM[ds.chartType] === 'polar'
  )
  
  if (allPolar) return 'polar'
  
  // Sinon, utiliser cartesian (permet le mélange)
  return 'cartesian'
}

// Types d'agrégation temporelle disponibles
export const TIME_AGGREGATIONS: TimeAggregationConfig[] = [
  {
    type: 'hour',
    label: 'Par heure',
    description: 'Agrégation heure par heure (00:00, 01:00, 02:00...)',
    smartDetection: true // Suggéré pour périodes < 3 jours
  },
  {
    type: 'day', 
    label: 'Par jour',
    description: 'Agrégation jour par jour',
    smartDetection: true // Suggéré pour périodes 3 jours - 3 mois
  },
  {
    type: 'week',
    label: 'Par semaine',
    description: 'Agrégation par semaine (lun-dim)',
    smartDetection: true // Suggéré pour périodes 1-12 mois
  },
  {
    type: 'month',
    label: 'Par mois',
    description: 'Agrégation mensuelle',
    smartDetection: true // Suggéré pour périodes > 6 mois
  },
  {
    type: 'year',
    label: 'Par année',
    description: 'Agrégation annuelle',
    smartDetection: true // Suggéré pour périodes > 2 ans
  },
  {
    type: 'hour_distribution',
    label: 'Répartition horaire',
    description: 'Regroupement par heure de la journée (0-23h)',
    smartDetection: false // Choix manuel pour analyse des heures de pointe
  },
  {
    type: 'weekday',
    label: 'Jour de la semaine',
    description: 'Regroupement par jour de la semaine (lun-dim)',
    smartDetection: false // Choix manuel pour analyse saisonnière
  },
  {
    type: 'month_day',
    label: 'Jour du mois',
    description: 'Regroupement par jour du mois (1-31)',
    smartDetection: false // Choix manuel pour cycles mensuels
  }
]

// Types d'agrégation statistique disponibles
export const AGGREGATION_TYPES: AggregationConfig[] = [
  {
    type: 'sum',
    label: 'Somme',
    description: 'Addition de toutes les valeurs',
    useCases: ['Chiffre d\'affaires total', 'Quantités vendues', 'Revenus'],
    compatibleFields: ['currency', 'number', 'integer']
  },
  {
    type: 'count',
    label: 'Nombre',
    description: 'Comptage du nombre d\'éléments',
    useCases: ['Nombre de ventes', 'Nombre de clients', 'Fréquence'],
    compatibleFields: ['any'] // Compatible avec tous les types
  },
  {
    type: 'average',
    label: 'Moyenne',
    description: 'Valeur moyenne arithmétique',
    useCases: ['Panier moyen', 'Prix moyen', 'Durée moyenne'],
    compatibleFields: ['currency', 'number', 'integer', 'duration']
  },
  {
    type: 'median',
    label: 'Médiane',
    description: 'Valeur centrale (50e percentile)',
    useCases: ['Prix médian', 'Analyse de distribution', 'Détection outliers'],
    compatibleFields: ['currency', 'number', 'integer']
  },
  {
    type: 'max',
    label: 'Maximum',
    description: 'Valeur la plus élevée',
    useCases: ['Pic de ventes', 'Plus gros achat', 'Record'],
    compatibleFields: ['currency', 'number', 'integer', 'date']
  },
  {
    type: 'min',
    label: 'Minimum',
    description: 'Valeur la plus basse',
    useCases: ['Plus petit achat', 'Minimum de stock', 'Première vente'],
    compatibleFields: ['currency', 'number', 'integer', 'date']
  },
  {
    type: 'std_dev',
    label: 'Écart-type',
    description: 'Mesure de la dispersion des données',
    useCases: ['Volatilité des ventes', 'Régularité des prix', 'Analyse de variance'],
    compatibleFields: ['currency', 'number', 'integer']
  },
  {
    type: 'variance',
    label: 'Variance',
    description: 'Carré de l\'écart-type (dispersion²)',
    useCases: ['Analyse statistique avancée', 'Modélisation', 'Recherche'],
    compatibleFields: ['currency', 'number', 'integer']
  },
  {
    type: 'first',
    label: 'Première valeur',
    description: 'Première valeur dans l\'ordre chronologique',
    useCases: ['Prix de départ', 'État initial', 'Baseline'],
    compatibleFields: ['any'] // Compatible avec tous les types
  },
  {
    type: 'last',
    label: 'Dernière valeur',
    description: 'Dernière valeur dans l\'ordre chronologique',
    useCases: ['État actuel', 'Dernière valeur connue', 'Évolution finale'],
    compatibleFields: ['any'] // Compatible avec tous les types
  },
  {
    type: 'percentile_25',
    label: '25e percentile (Q1)',
    description: '25% des valeurs sont inférieures à ce seuil',
    useCases: ['Analyse de distribution', 'Seuils bas', 'Performances faibles'],
    compatibleFields: ['currency', 'number', 'integer']
  },
  {
    type: 'percentile_75',
    label: '75e percentile (Q3)',
    description: '75% des valeurs sont inférieures à ce seuil',
    useCases: ['Analyse de distribution', 'Seuils hauts', 'Bonnes performances'],
    compatibleFields: ['currency', 'number', 'integer']
  },
  {
    type: 'percentile_90',
    label: '90e percentile',
    description: '90% des valeurs sont inférieures à ce seuil',
    useCases: ['Détection outliers', 'Top performers', 'Seuils élevés'],
    compatibleFields: ['currency', 'number', 'integer']
  },
  {
    type: 'percentile_95',
    label: '95e percentile',
    description: '95% des valeurs sont inférieures à ce seuil',
    useCases: ['Valeurs exceptionnelles', 'SLA élevés', 'Analyses de risque'],
    compatibleFields: ['currency', 'number', 'integer']
  }
]

// Options de types de graphiques
export interface ChartTypeOption {
  type: FlexibleChartType
  label: string
  description: string
  icon: string
  mobileOptimized: boolean
  supportedDataSources: DataSource[]
  maxDataSources: number
  supportsSecondaryAxis: boolean
}

export const FLEXIBLE_CHART_TYPE_OPTIONS: ChartTypeOption[] = [
  {
    type: 'line',
    label: 'Courbe',
    description: 'Évolution dans le temps',
    icon: '📈',
    mobileOptimized: true,
    supportedDataSources: ['sales', 'products'],
    maxDataSources: 5,
    supportsSecondaryAxis: true
  },
  {
    type: 'bar',
    label: 'Barres',
    description: 'Comparaison de valeurs',
    icon: '📊',
    mobileOptimized: true,
    supportedDataSources: ['sales', 'products'],
    maxDataSources: 10,
    supportsSecondaryAxis: true
  },
  {
    type: 'pie',
    label: 'Secteurs',
    description: 'Répartition proportionnelle',
    icon: '🥧',
    mobileOptimized: true,
    supportedDataSources: ['sales', 'products'],
    maxDataSources: 1,
    supportsSecondaryAxis: false
  },
  {
    type: 'doughnut',
    label: 'Anneau',
    description: 'Répartition avec centre libre',
    icon: '🍩',
    mobileOptimized: true,
    supportedDataSources: ['sales', 'products'],
    maxDataSources: 1,
    supportsSecondaryAxis: false
  },
  {
    type: 'area',
    label: 'Aires empilées',
    description: 'Évolution avec volumes',
    icon: '📊',
    mobileOptimized: false, // Plus complexe à lire sur mobile
    supportedDataSources: ['sales', 'products'],
    maxDataSources: 5,
    supportsSecondaryAxis: false
  }
]

// ========================================
// FONCTIONS UTILITAIRES
// ========================================

// Détection automatique intelligente de l'agrégation temporelle
export function getSmartTimeAggregation(startDate: Date, endDate: Date): TimeAggregation {
  const diffDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  
  if (diffDays <= 3) return 'hour'        // ≤ 3 jours → par heure
  if (diffDays <= 90) return 'day'        // ≤ 3 mois → par jour  
  if (diffDays <= 365) return 'week'      // ≤ 1 an → par semaine
  if (diffDays <= 730) return 'month'     // ≤ 2 ans → par mois
  return 'year'                           // > 2 ans → par année
}

// Suggestions d'agrégation intelligentes basées sur le type de champ
export function getSmartAggregationSuggestions(fieldType: string, fieldName: string): AggregationConfig[] {
  const suggestions = AGGREGATION_TYPES.filter(agg => 
    agg.compatibleFields.includes(fieldType) || 
    agg.compatibleFields.includes('any')
  )
  
  // Prioriser selon le type de champ
  if (fieldType === 'currency') {
    // Inclure toutes les analyses statistiques pour les montants monétaires
    return suggestions.filter(s => [
      'sum', 'average', 'max', 'min', 'median', 'std_dev', 
      'percentile_25', 'percentile_75', 'percentile_90'
    ].includes(s.type))
  }
  
  if (fieldType === 'integer' && fieldName.toLowerCase().includes('quantity')) {
    return suggestions.filter(s => ['sum', 'count', 'average', 'max', 'min', 'std_dev'].includes(s.type))
  }
  
  if (fieldType === 'integer' || fieldType === 'number') {
    return suggestions.filter(s => [
      'sum', 'count', 'average', 'max', 'min', 'median', 'std_dev',
      'percentile_25', 'percentile_75'
    ].includes(s.type))
  }
  
  return suggestions.slice(0, 6) // Augmenter à 6 suggestions pour inclure plus d'options
}

// Création d'une configuration par défaut
export function createDefaultFlexibleChartConfig(): FlexibleChartConfig {
  return {
    chartType: 'line', // Gardé pour rétrocompatibilité
    coordinateSystem: 'cartesian', // Nouveau système par défaut
    dataSources: [{
      id: 'ds_1',
      name: 'Série 1',
      source: 'sales',
      aggregation: 'sum',
      field: 'excluded_tax',
      limit: 100,
      origin: 'all',
      deposit: false,
      chartType: 'line' // Type par défaut pour la série
    }],
    xAxis: {
      type: 'time',
      field: 'date',
      label: 'Période',
      timeAggregation: 'day'
    },
    yAxis: {
      type: 'linear',
      field: 'value',
      label: 'Valeurs'
    },
    filters: {},
    display: {
      showLegend: true,
      responsive: true,
      mobileOptimized: true
    },
    mobileConfig: {
      touchOptimized: true,
      compactMode: false,
      swipeNavigation: true,
      bottomSheetMode: true
    },
    period: {
      start: '',
      end: ''
    }
  }
}

// Générer un ID unique pour les sources de données
export function generateDataSourceId(): string {
  return 'ds_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6)
}

// ========================================
// CONFIGURATION DES CHAMPS PAR SOURCE
// ========================================

// Métadonnées des champs disponibles
export interface FieldMetadata {
  key: string
  label: string
  description: string
  type: 'currency' | 'number' | 'integer' | 'percentage' | 'string' | 'boolean'
  category: 'revenue' | 'volume' | 'performance' | 'identification' | 'metric'
  suggestedAggregations: AggregationType[]
}

// Champs disponibles pour la source 'sales' (SalesByIntervalResponse)
export const SALES_FIELDS: FieldMetadata[] = [
  {
    key: 'excluded_tax',
    label: 'Montant HT',
    description: 'Chiffre d\'affaires hors taxes',
    type: 'currency',
    category: 'revenue',
    suggestedAggregations: ['sum', 'average', 'max', 'min', 'median', 'std_dev', 'percentile_25', 'percentile_75']
  },
  {
    key: 'included_tax',
    label: 'Montant TTC', 
    description: 'Chiffre d\'affaires toutes taxes comprises',
    type: 'currency',
    category: 'revenue',
    suggestedAggregations: ['sum', 'average', 'max', 'min', 'median', 'std_dev', 'percentile_25', 'percentile_75']
  },
  {
    key: 'total_paid',
    label: 'Total encaissé',
    description: 'Montant total réellement encaissé',
    type: 'currency',
    category: 'revenue',
    suggestedAggregations: ['sum', 'average', 'max', 'min', 'median', 'std_dev', 'percentile_25', 'percentile_75']
  },
  {
    key: 'total_documents',
    label: 'Nombre de transactions',
    description: 'Nombre de documents/factures',
    type: 'integer',
    category: 'volume',
    suggestedAggregations: ['sum', 'count', 'average', 'max', 'min', 'std_dev']
  },
  {
    key: 'average_transaction',
    label: 'Panier moyen',
    description: 'Valeur moyenne par transaction (calculé)',
    type: 'currency',
    category: 'metric',
    suggestedAggregations: ['average', 'median', 'max', 'min', 'std_dev', 'sum']
  },
  {
    key: 'tax_amount',
    label: 'Montant des taxes',
    description: 'Différence TTC - HT (calculé)',
    type: 'currency',
    category: 'metric',
    suggestedAggregations: ['sum', 'average', 'max', 'min', 'median', 'std_dev', 'percentile_25', 'percentile_75']
  }
]

// Champs disponibles pour la source 'products' (ProductsReportResponse)
export const PRODUCTS_FIELDS: FieldMetadata[] = [
  // Identification
  {
    key: 'product_id',
    label: 'ID Produit',
    description: 'Identifiant unique du produit',
    type: 'integer',
    category: 'identification',
    suggestedAggregations: ['count', 'min', 'max']
  },
  {
    key: 'product_name',
    label: 'Nom du produit',
    description: 'Nom commercial du produit',
    type: 'string',
    category: 'identification',
    suggestedAggregations: ['count', 'first', 'last']
  },
  {
    key: 'family_name',
    label: 'Famille de produit',
    description: 'Catégorie/famille du produit',
    type: 'string',
    category: 'identification',
    suggestedAggregations: ['count']
  },
  
  // Revenus
  {
    key: 'excluded_tax',
    label: 'CA HT produit',
    description: 'Chiffre d\'affaires hors taxes du produit',
    type: 'currency',
    category: 'revenue',
    suggestedAggregations: ['sum', 'average', 'max', 'min', 'median', 'std_dev', 'percentile_25', 'percentile_75']
  },
  {
    key: 'included_tax',
    label: 'CA TTC produit',
    description: 'Chiffre d\'affaires TTC du produit',
    type: 'currency',
    category: 'revenue',
    suggestedAggregations: ['sum', 'average', 'max', 'min', 'median', 'std_dev', 'percentile_25', 'percentile_75']
  },
  {
    key: 'average_sale_value',
    label: 'Valeur moyenne de vente',
    description: 'Prix de vente moyen du produit',
    type: 'currency',
    category: 'metric',
    suggestedAggregations: ['average', 'median', 'max', 'min', 'std_dev', 'sum']
  },
  
  // Volumes
  {
    key: 'line_count',
    label: 'Nombre de ventes',
    description: 'Nombre de lignes de vente',
    type: 'integer',
    category: 'volume',
    suggestedAggregations: ['sum', 'count', 'average']
  },
  
  // Performance et parts de marché
  {
    key: 'market_share_revenue',
    label: 'Part de marché (CA)',
    description: 'Part du produit dans le CA total',
    type: 'percentage',
    category: 'performance',
    suggestedAggregations: ['average', 'max', 'min']
  },
  {
    key: 'market_share_volume',
    label: 'Part de marché (volume)',
    description: 'Part du produit en volume',
    type: 'percentage',
    category: 'performance',
    suggestedAggregations: ['average', 'max', 'min']
  },
  {
    key: 'tax_efficiency',
    label: 'Efficacité fiscale',
    description: 'Score d\'efficacité fiscale',
    type: 'number',
    category: 'performance',
    suggestedAggregations: ['average', 'max', 'min']
  },
  {
    key: 'volume_efficiency',
    label: 'Efficacité volume',
    description: 'Score d\'efficacité en volume',
    type: 'number',
    category: 'performance',
    suggestedAggregations: ['average', 'max', 'min']
  },
  
  // Position dans famille
  {
    key: 'rank_in_family',
    label: 'Rang dans la famille',
    description: 'Position du produit dans sa famille',
    type: 'integer',
    category: 'performance',
    suggestedAggregations: ['average', 'min', 'max']
  },
  {
    key: 'product_contribution_pct',
    label: 'Contribution famille (%)',
    description: 'Pourcentage de contribution à la famille',
    type: 'percentage',
    category: 'performance',
    suggestedAggregations: ['average', 'max', 'min']
  },
  {
    key: 'top_performer',
    label: 'Top performer',
    description: 'Produit identifié comme top performer',
    type: 'boolean',
    category: 'performance',
    suggestedAggregations: ['count']
  }
]

// Mapping des champs par source
export const FIELDS_BY_SOURCE: Record<DataSource, FieldMetadata[]> = {
  sales: SALES_FIELDS,
  products: PRODUCTS_FIELDS
}

// Utilitaire pour obtenir les champs d'une source
export function getFieldsForSource(source: DataSource): FieldMetadata[] {
  return FIELDS_BY_SOURCE[source] || []
}

// Utilitaire pour obtenir les métadonnées d'un champ
export function getFieldMetadata(source: DataSource, fieldKey: string): FieldMetadata | undefined {
  return getFieldsForSource(source).find(field => field.key === fieldKey)
}